"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Request = exports.Flow = void 0;
const logger_1 = require("@linq/logger");
const qs_1 = __importDefault(require("qs"));
const axios_1 = __importDefault(require("axios"));
const RequestRetry_1 = require("./RequestRetry");
var Flow;
(function (Flow) {
    Flow["Req"] = "REQUEST";
    Flow["Res"] = "RESPONSE";
})(Flow = exports.Flow || (exports.Flow = {}));
class Request {
    constructor(config) {
        this.config = config;
        this.retry = new RequestRetry_1.RequestRetry(this.config?.retryConfig);
        const axiosConfig = config?.axios ?? {};
        axiosConfig.baseURL = config?.baseUrl ?? axiosConfig.baseURL;
        this.baseUrl = axiosConfig.baseURL;
        this.instance = axios_1.default.create(axiosConfig);
        this.instance.defaults.headers.common['Content-Type'] = 'application/json';
        this.request = this.request.bind(this);
        this.get = this.get.bind(this);
        this.delete = this.delete.bind(this);
        this.post = this.post.bind(this);
        this.put = this.put.bind(this);
        this.patch = this.patch.bind(this);
        this.instance.interceptors.request.use(this.onRequestFullfilled.bind(this), this.onRequestFailure.bind(this));
        this.instance.interceptors.response.use(this.onResponseFullfilled.bind(this), this.onResponseRejection.bind(this));
    }
    getCurrentState(config) {
        const currentState = config?.linqRetry || { retryCount: 0 };
        if (config) {
            config.linqRetry = currentState;
        }
        return currentState;
    }
    onRequestFullfilled(requestConfig) {
        const currentState = this.getCurrentState(requestConfig);
        currentState.lastRequestTime = new Date().getTime();
        currentState.startTime = process.hrtime();
        return requestConfig;
    }
    onRequestFailure(error) {
        return error;
    }
    onResponseFullfilled(response) {
        const currentState = this.getCurrentState(response.config);
        currentState.endTime = process.hrtime(currentState.startTime);
        this.log(Flow.Res, response.config, this.getStatus(response));
        return response;
    }
    async onResponseRejection(error) {
        const config = error.config;
        if (!config) {
            logger_1.Logger.debug('could not obtain AxiosRequestConfig');
            throw error;
        }
        const currentState = this.getCurrentState(config);
        currentState.endTime = process.hrtime(currentState.startTime);
        this.log(Flow.Res, config, this.getStatus(error.response), error);
        currentState.retryCount += 1;
        const shouldRetry = this.retry.retryable(error) && currentState.retryCount < this.retry.maxCount;
        if (shouldRetry) {
            logger_1.Logger.debug(`request failure is retryable`);
            const delay = this.retry.computeRetryTime(currentState.retryCount);
            const remainingCount = this.retry.maxCount - currentState.retryCount;
            // determine if response has x-ratelimit headers to use for delaying retry attempts
            await this.waitForLimitReset(error.response);
            // continue with normal exponential backoff with full jitter randomization
            logger_1.Logger.debug(`retrying request ${config.url} in ${delay}ms, ${remainingCount} attempt(s) remaining...`);
            return new Promise((resolve) => {
                return setTimeout(() => {
                    return resolve(this.request(config));
                }, delay);
            });
        }
        throw error;
    }
    log(flow, config, status, err) {
        const currentState = this.getCurrentState(config);
        const stats = {
            responseTime: this.getResponseTime(currentState),
            lastRequestStartTime: currentState.retryCount && currentState.lastRequestTime
                ? new Date(currentState.lastRequestTime).toISOString()
                : undefined,
            retryCount: currentState.retryCount || undefined,
        };
        Object.keys(stats).forEach((statKey) => {
            if (stats[statKey] === undefined) {
                delete stats[statKey];
            }
        });
        const method = config.method;
        const url = this.baseUrl && !config.url?.includes('http') ? `${this.baseUrl}${config.url}` : config.url;
        if (method && url) {
            const correlationId = logger_1.RequestContext.store()?.get('correlationId')
                ? `[${logger_1.RequestContext.store()?.get('correlationId')}]`
                : '';
            if (flow === Flow.Req) {
                if (config.params) {
                    logger_1.Logger.debug(`${correlationId} ${method.toUpperCase()}[${currentState.retryCount}] |--> ${url}`.trim(), 'queryParams', config.params);
                }
                else {
                    logger_1.Logger.debug(`${correlationId} ${method.toUpperCase()}[${currentState.retryCount}] |--> ${url}`.trim());
                }
            }
            else {
                this.logResponse(correlationId, config, stats, err, status);
            }
        }
        else {
            logger_1.Logger.error('failed to extract method and url from AxiosRequestConfig');
        }
    }
    logResponse(correlationId, config, stats, err, status) {
        const currentState = this.getCurrentState(config);
        const url = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
        const log = `${correlationId} ${config.method?.toUpperCase()}[${currentState.retryCount}] [${status}] |<-- ${url}`.trim();
        if (err?.response) {
            logger_1.Logger.error(log, err.response.data, stats);
        }
        else if (err?.request) {
            logger_1.Logger.error(log, err.request.message, stats);
        }
        else if (err) {
            logger_1.Logger.error(log, err.message, stats);
        }
        else {
            logger_1.Logger.debug(log, stats);
        }
    }
    getResponseTime(currentState) {
        let responseTime;
        if (currentState.endTime?.length === 2) {
            responseTime = `${currentState.endTime[0]}s ${(currentState.endTime[1] / 1e6).toFixed()}ms`;
        }
        return responseTime;
    }
    getStatus(r) {
        return r?.status;
    }
    async request(config) {
        this.log(Flow.Req, config);
        const response = await this.instance.request(config);
        return response;
    }
    async post(url, data, config) {
        // auto-encode application/x-www-form-urlencoded POST requests
        let body = data;
        if (data && config?.headers?.['Content-Type'] === 'application/x-www-form-urlencoded' && typeof data !== 'string') {
            logger_1.Logger.debug('encoding payload to form-urlencoded');
            body = qs_1.default.stringify(data);
        }
        if (config) {
            config.url = url;
            config.data = body;
            config.method = 'post';
        }
        const axiosConifg = config ?? { url, data: body, method: 'post' };
        return this.request(axiosConifg);
    }
    async get(url, config) {
        if (config) {
            config.url = url;
            config.method = 'get';
        }
        const axiosConifg = config ?? { url, method: 'get' };
        return this.request(axiosConifg);
    }
    async put(url, data, config) {
        if (config) {
            config.url = url;
            config.data = data;
            config.method = 'put';
        }
        const axiosConifg = config ?? { url, data, method: 'put' };
        return this.request(axiosConifg);
    }
    async patch(url, data, config) {
        if (config) {
            config.url = url;
            config.data = data;
            config.method = 'patch';
        }
        const axiosConifg = config ?? { url, data, method: 'patch' };
        return this.request(axiosConifg);
    }
    async delete(url, data, config) {
        if (config) {
            config.url = url;
            config.data = data;
            config.method = 'delete';
        }
        const axiosConifg = config ?? { url, data, method: 'delete' };
        return this.request(axiosConifg);
    }
    success(response) {
        return response.data;
    }
    error(error) {
        throw error;
    }
    /**
     * determines if custom x-ratelimit response headers are in use and returned by a failed request.
     * since we are already in an error condition (429 status) wait until the limit reset time before
     * attempting to retry.  after the reset time still use the exponential backoff with full jitter delay
     * to scatter parallel requests and reduce the load.  So this is an additonal forced wait time.
     * @param response
     * @returns
     */
    async waitForLimitReset(response) {
        const rateLimitRemaining = response?.headers['x-ratelimit-remaining'];
        const rateLimitReset = response?.headers['x-ratelimit-reset'];
        if (!rateLimitRemaining || !rateLimitReset) {
            return;
        }
        logger_1.Logger.debug('x-ratelimit-remaining', rateLimitRemaining);
        logger_1.Logger.debug('x-ratelimit-reset', rateLimitReset);
        const resetTimestamp = Number(rateLimitReset);
        const now = Math.floor(new Date().getTime() / 1000); // unix epoch in seconds
        const delta = resetTimestamp - now;
        if (delta > 0) {
            logger_1.Logger.debug(`waiting ${delta} seconds for throttling limit reset`);
            await this.sleep(delta * 1000);
        }
    }
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
exports.Request = Request;
//# sourceMappingURL=Request.js.map